import Handsontable from 'handsontable/base';
import { CellData, CellDataChange, TableContentData } from '../../../../../common/table/TableTypes';
import { cloneDeep, get, isEqual } from 'lodash';
import { buildHotTableContentData } from './hotTableDataUtils';
import { getFirstSelection, getStartCol, getStartRow } from '../selectionUtils/tableSelectionAccessors';
import { excludeRefHeadersFromSelection, getAllCellsBetween } from '../selectionUtils/tableSelectionGeometryUtils';
import { isBackgroundStyle, isColorStyle } from '../../../../components/editor/editorUtils';
import { convertFromRaw, convertToRaw, EditorState, Modifier } from 'draft-js';
import getSelectionStateForContent from '../../../../components/editor/customRichUtils/selection/getSelectionStateForContent';
import {
    TEXT_BACKGROUND_PRESETS,
    TEXT_COLOR_PRESETS,
} from '../../../../components/editor/plugins/textColor/textColorConstants';
import { doesTableCellDataHaveInlineStyles } from '../dataUtils/cell/tableCellDraftContentUtils';
import rawGetText from '../../../../../common/utils/editor/rawUtils/rawGetText';
import { setHotTableCellMetaObject } from './meta/hotTableCellMetaUtils';
import { isHotInstanceAvailable } from './hotTableInstanceValidationUtils';

const EMPTY_CELL_DATA: CellData = { value: null };

/**
 * Returns the stored cell data for a coordinate, falling back to an empty cell shape when metadata is missing.
 */
const getHotTableCellDataForChange = (hotTableData: TableContentData, row: number, col: number): CellData =>
    cloneDeep(get(hotTableData, [row, col]) || EMPTY_CELL_DATA);

/**
 * - Force Handsontable to repaint cells so metadata-only changes like background, text style, etc. become visible
 * - Recalculate table layout after the repaint, so row heights and table dimensions match the new rendered
 *   content. This matters when a change affects wrapping, rich text, renderer structure, or cell type.
 */
const renderAndResizeHotTable = (hotInstance: Handsontable): void => {
    // `requestAnimationFrame` is used for calling render here as it improves performance
    requestAnimationFrame(() => {
        if (!isHotInstanceAvailable(hotInstance)) return;

        // Render recent changes
        hotInstance.render();

        // Ensure row heights are correct
        hotInstance.refreshDimensions();
    });
};

/**
 * Applies concrete cell-data replacements to the HOT instance without merging with existing metadata.
 *
 * NOTE: This will *not* automatically trigger a redux sync. You'll need to manually use
 *   hotInstance.runHooks('onMilanoteMarkDirty', source) after this function to trigger the sync.
 */
export const applyExactHotTableCellDataChanges = (
    hotInstance: Handsontable,
    cellDataChanges: CellDataChange[],
    source: string,
): void => {
    if (!cellDataChanges?.length) return;

    const hotTableData = buildHotTableContentData(hotInstance);
    const newData = cloneDeep(hotTableData);

    hotInstance.suspendRender();

    const referencedCellUpdates: Array<[number, number, Handsontable.CellValue]> = [];

    const formulasPlugin = hotInstance.getPlugin('formulas');
    const hyperFormulaEngine = formulasPlugin.engine;
    const sheetId = formulasPlugin.sheetId;

    cellDataChanges.forEach(([row, col, _previousCellData, newCellData]) => {
        const numericCol = Number(col);
        const exactCellData = cloneDeep(newCellData);

        newData[row][numericCol] = exactCellData;

        setHotTableCellMetaObject(hotInstance, row, numericCol, { cellData: exactCellData });

        // If the current cell has dependants, we need to update the values so that afterChange can run
        if (!!hyperFormulaEngine && !!sheetId) {
            const cellDependents = hyperFormulaEngine.getCellDependents({ sheet: sheetId, row, col: numericCol });
            if (cellDependents.length) referencedCellUpdates.push([row, numericCol, exactCellData.value]);
        }
    });

    hotInstance.resumeRender();
    renderAndResizeHotTable(hotInstance);

    if (referencedCellUpdates.length === 0) return;

    // Update cells that are referenced so that cell type changes can be applied to the dependents
    // This must run after the table element has been updated, because any dependent cell type
    // changes through setDataAtCell > afterChange will also be updated through this function recursively,
    // and we don't want the inner table data updates to happen before the original one
    //
    // There is a Handsontable bug where setDataAtCell only resolves source properly if passed as 4th param,
    // despite the type overloads saying otherwise
    // TODO-HANDSONTABLE-UPGRADE: Check whether this is still a bug after upgrading Handsontable
    // @ts-ignore - This addition was pulled from a bug that Marisa fixed
    hotInstance.setDataAtCell(referencedCellUpdates, undefined, undefined, source);
};

type HotTableTextStyleUpdate = {
    cellDataChanges: CellDataChange[];
    newData: TableContentData;
};

/**
 * Builds the full cell-data changes needed to apply or remove a text style across the current selection.
 */
export const buildHotTableTextStyleUpdate = (
    hotInstance: Handsontable,
    textStyle: string | null,
    cellSelections = hotInstance.getSelected(),
): HotTableTextStyleUpdate | undefined => {
    if (!cellSelections) return;

    const hotTableData = buildHotTableContentData(hotInstance);

    // ****************************************************
    // Determine whether to add/remove inline style based on the first cell selection

    const firstCellSelection = getFirstSelection(cellSelections);
    if (!firstCellSelection) return;

    // Remove any header cells from the selection
    const validCellSelection = excludeRefHeadersFromSelection(firstCellSelection);

    const selectedCellData = getHotTableCellDataForChange(
        hotTableData,
        getStartRow(validCellSelection),
        getStartCol(validCellSelection),
    );

    const shouldAddInlineStyle = textStyle ? !doesTableCellDataHaveInlineStyles(selectedCellData, textStyle) : true;
    const isColorOrBackgroundTextStyleChange = !textStyle || isColorStyle(textStyle) || isBackgroundStyle(textStyle);

    // ****************************************************
    // Apply/remove inline style to all selected cells

    const newData = cloneDeep(hotTableData);
    const cellDataChanges: CellDataChange[] = [];

    getAllCellsBetween(cellSelections).forEach(({ row, col }) => {
        const previousCellData = getHotTableCellDataForChange(hotTableData, row, col);
        const nextCellData = cloneDeep(previousCellData);

        // Add/remove text style from cellData.textStyle

        let newTextStyle = nextCellData.textStyle || [];
        if (shouldAddInlineStyle) {
            newTextStyle = newTextStyle.filter((currentTextStyle) => {
                if (isColorOrBackgroundTextStyleChange) {
                    return !(isColorStyle(currentTextStyle) || isBackgroundStyle(currentTextStyle));
                }

                return currentTextStyle !== textStyle;
            });

            textStyle && newTextStyle.push(textStyle);
        } else {
            newTextStyle = newTextStyle.filter((style) => style !== textStyle);
        }

        nextCellData.textStyle = newTextStyle;

        // Add/remove text style from inline style of cellData.draftContent
        if (previousCellData.draftContent) {
            // If a color/background text style is added, remove all other existing color/background inline text styles
            if (shouldAddInlineStyle && isColorOrBackgroundTextStyleChange) {
                let contentState = convertFromRaw(previousCellData.draftContent);
                let newEditorState = EditorState.createWithContent(contentState);

                const selectionState = getSelectionStateForContent(contentState);

                [...Object.values(TEXT_COLOR_PRESETS), ...Object.values(TEXT_BACKGROUND_PRESETS)].forEach((style) => {
                    contentState = Modifier.removeInlineStyle(contentState, selectionState, style);
                });

                newEditorState = EditorState.push(newEditorState, contentState, 'change-inline-style');

                nextCellData.draftContent = convertToRaw(newEditorState.getCurrentContent());
                nextCellData.value = rawGetText(nextCellData.draftContent);
            }

            // If the style needs to be removed, ensure that the styling is removed from Draft format as well
            if (!shouldAddInlineStyle && textStyle) {
                const previousContentState = convertFromRaw(previousCellData.draftContent);

                let newEditorState = EditorState.createWithContent(previousContentState);

                const selectionState = getSelectionStateForContent(previousContentState);

                const newContentState = Modifier.removeInlineStyle(previousContentState, selectionState, textStyle);
                newEditorState = EditorState.push(newEditorState, newContentState, 'change-inline-style');

                nextCellData.draftContent = convertToRaw(newEditorState.getCurrentContent());
                nextCellData.value = rawGetText(nextCellData.draftContent);
            }
        }

        if (isEqual(previousCellData, nextCellData)) return;

        newData[row][col] = cloneDeep(nextCellData);
        cellDataChanges.push([row, col, previousCellData, cloneDeep(nextCellData)]);
    });

    if (!cellDataChanges.length) return;

    return { cellDataChanges, newData };
};
